/**
 * Reading the ID token's payload, client-side.
 *
 * **This is not verification and must never be treated as it.** Anyone can mint
 * a JWT with `isAdmin: true` in it; the signature is what makes a claim true,
 * and only the API checks that (see apps/api/src/auth/identity.ts, which
 * verifies against Google's JWKS on every request). What the browser decodes
 * here is used for exactly two things, both of which are safe to be wrong
 * about:
 *
 *  - **when to refresh** — `exp`, so we can re-prompt before the token lapses;
 *  - **what to draw** — a name and email in the corner while `GET /me` loads.
 *
 * Every authorization decision in this app comes from `GET /me` or from a route
 * the API answered, never from these claims. The UI hides buttons the caller
 * cannot use; the API is what actually refuses them.
 */

/** The claims we read. Google sends more; nothing else is our business. */
export interface TokenClaims {
  sub: string;
  email: string;
  name: string;
  picture?: string;
  /** Seconds since the epoch, per the JWT spec — *not* milliseconds. */
  exp: number;
}

/**
 * Tolerance for a clock that disagrees with Google's.
 *
 * A laptop a minute fast would otherwise consider a freshly minted token
 * already expired and sign the user straight back out, in a loop. Treating the
 * last 30 seconds as already-gone costs nothing: the refresh fires two minutes
 * ahead of this anyway.
 */
export const CLOCK_SKEW_SECONDS = 30;

/**
 * base64url → JSON.
 *
 * Two things the naive version gets wrong:
 *
 *  - **Padding.** base64url strips `=`. A JWT payload's length mod 4 happens to
 *    never be 1, so `atob` tolerates it in practice — but this helper does not
 *    get to rely on a Google implementation detail, so the padding goes back on.
 *  - **UTF-8.** `atob` yields one byte per char, so a name with an accent in it
 *    decodes to mojibake. Re-reading those bytes through `TextDecoder` is what
 *    makes non-ASCII names come out right. (DragDuro uses the older
 *    `decodeURIComponent(escape(...))` trick for this; same intent, deprecated
 *    primitives.)
 */
function decodeSegment(segment: string): unknown {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/**
 * Decode, or `null` for anything that is not a usable token.
 *
 * Returns rather than throws: a corrupt token is not an exceptional condition,
 * it is a signed-out user. Callers treat `null` as "no session" and move on.
 */
export function decodeToken(jwt: string): TokenClaims | null {
  const payload = jwt.split('.')[1];
  if (!payload) return null;

  try {
    const claims = decodeSegment(payload) as Record<string, unknown>;
    const { sub, email, exp } = claims;
    // Without `exp` there is nothing to schedule a refresh against, and a token
    // that never expires is not one Google issued. Reject it rather than hold a
    // session we can never renew.
    if (typeof sub !== 'string' || typeof email !== 'string' || typeof exp !== 'number') return null;

    return {
      sub,
      email,
      // Google omits `name` for an account with no profile name, the same case
      // the API falls back on. Match its fallback so the two never disagree.
      name: typeof claims.name === 'string' && claims.name.trim() ? claims.name : (email.split('@')[0] ?? email),
      ...(typeof claims.picture === 'string' ? { picture: claims.picture } : {}),
      exp,
    };
  } catch {
    return null;
  }
}

export function isExpired(claims: TokenClaims, now: Date = new Date()): boolean {
  return now.getTime() / 1000 > claims.exp - CLOCK_SKEW_SECONDS;
}

/** Milliseconds until `exp`, before the skew allowance. Negative once past. */
export function millisUntilExpiry(claims: TokenClaims, now: Date = new Date()): number {
  return claims.exp * 1000 - now.getTime();
}
